/**
 * Low-level Supabase fetchers / mutators for the `offerings` table.
 *
 * These functions intentionally contain no React-Query state — they are
 * called from inside the hooks in this directory. Keep behavior identical
 * to the original inline implementations.
 */

import { getBrowserSupabaseClient } from "@wtc/supabase";
import type { Offering } from "@/lib/types/database/offerings";
import { SCALAR_FIELDS } from "@/lib/graphql/serialize-input";
import { coerceBigFloatFieldsToNumber } from "@/lib/graphql/coerce-bigfloat-output";

const OFFERING_DECIMAL_FIELDS = SCALAR_FIELDS.offerings.bigFloat;

/**
 * Coerce BigFloat columns to numbers so the `number`-typed `Offering` model is
 * honest. Idempotent — PostgREST rows are already numeric; this protects any
 * Apollo-sourced rows and removes scattered defensive `Number(...)` at callers.
 */
function normalizeOffering(offering: Offering): Offering {
  return coerceBigFloatFieldsToNumber(offering, OFFERING_DECIMAL_FIELDS);
}

export interface PaginatedOfferingsResult {
  data: Offering[];
  totalCount: number;
  pageInfo: {
    hasNextPage: boolean;
    endCursor: string;
  };
}

export async function fetchOfferingsPaginated(
  first: number,
  offset: number,
  filter: Record<string, unknown> | undefined
): Promise<PaginatedOfferingsResult> {
  const supabase = getBrowserSupabaseClient();
  let q = supabase
    .from("offerings")
    .select("*", { count: "exact" })
    .order("title", { ascending: true })
    .range(offset, offset + first - 1);

  // Apply filters (eq, gte, lte) if provided — kept inline to preserve
  // Supabase's fluent builder typing across chained method calls.
  if (filter && typeof filter === "object") {
    for (const [key, value] of Object.entries(filter)) {
      if (key.startsWith("_")) continue; // Skip internal keys like _search
      if (value == null || typeof value !== "object") continue;
      const v = value as Record<string, unknown>;
      if ("eq" in v && v.eq != null) {
        q = q.eq(key, v.eq);
      } else {
        if ("gte" in v && v.gte != null && !Number.isNaN(Number(v.gte))) {
          q = q.gte(key, Number(v.gte));
        }
        if ("lte" in v && v.lte != null && !Number.isNaN(Number(v.lte))) {
          q = q.lte(key, Number(v.lte));
        }
      }
    }
  }

  const { data, error, count } = await q;

  if (error) throw error;

  const totalCount = count ?? 0;
  const dataLength = data?.length ?? 0;

  return {
    data: ((data || []) as Offering[]).map(normalizeOffering),
    totalCount,
    pageInfo: {
      hasNextPage: offset + dataLength < totalCount,
      endCursor: String(offset + dataLength),
    },
  };
}

export async function fetchOfferingById(id: string): Promise<Offering> {
  const supabase = getBrowserSupabaseClient();
  const { data, error } = await supabase.from("offerings").select("*").eq("id", id).single();
  if (error) throw error;
  return normalizeOffering(data as Offering);
}

/**
 * Strip keys beginning with `__` (internal/synthetic fields not stored in DB).
 */
function stripInternalKeys(input: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(input).filter(([key]) => !key.startsWith("__")));
}

export async function insertOffering(input: Partial<Offering> & { id?: string }): Promise<Offering> {
  const cleanInput = stripInternalKeys(input);

  if (!cleanInput.title) {
    throw new Error("Title is required to create an offering");
  }

  const supabase = getBrowserSupabaseClient();
  const { data, error } = await supabase
    .from("offerings")
    .insert(cleanInput)
    .select()
    .single();

  if (error) throw error;
  return normalizeOffering(data as Offering);
}

export async function updateOffering(input: Partial<Offering> & { id: string }): Promise<Offering> {
  const { id, ...updateInput } = input;
  const cleanInput = stripInternalKeys(updateInput);

  const supabase = getBrowserSupabaseClient();
  const { data, error } = await supabase
    .from("offerings")
    .update(cleanInput)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return normalizeOffering(data as Offering);
}

export async function deleteOffering(id: string): Promise<string> {
  const supabase = getBrowserSupabaseClient();
  const { error } = await supabase.from("offerings").delete().eq("id", id);
  if (error) throw error;
  return id;
}
